// src/pages/ventes/NouveauDevis.jsx
import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Form, Button, Table, Spinner, Badge,
    Modal, InputGroup
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";

/* ============================================================
   TOAST NOTIFICATION — Design charte
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 4000 }) => {
    if (!show) return null;

    const toastConfig = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    };
    const config = toastConfig[type] || toastConfig.info;

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom">
                    <p className="mb-0">{message}</p>
                </div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function NouveauDevis() {
    const navigate = useNavigate();
    const [saving, setSaving] = useState(false);
    const [clients, setClients] = useState([]);
    const [produits, setProduits] = useState([]);
    const [showProduitModal, setShowProduitModal] = useState(false);
    const [searchProduit, setSearchProduit] = useState("");
    const [toast, setToast] = useState({ show: false, type: 'success', title: '', message: '' });

    const [taxes, setTaxes] = useState({
        tva_taux: 18,
        css_taux: 1,
        tva_active: true,
        css_active: true
    });

    const [devis, setDevis] = useState({
        id_client: '',
        date_proforma: new Date().toISOString().split('T')[0],
        date_validite: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        remise_globale: 0,
        notes: '',
        statut: 'brouillon',
        lignes: []
    });

    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: 'success', title: '', message: '' });

    useEffect(() => {
        fetchClients();
        fetchProduits();
        fetchTaxes();
    }, []);

    const fetchTaxes = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/parametres.php?action=taxes`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                if (result.data) setTaxes(result.data);
            }
        } catch (err) {
            console.error("Erreur chargement taxes", err);
        }
    };

    const fetchClients = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/clients.php`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                setClients(result.data || []);
            }
        } catch (err) {
            console.error("Erreur chargement clients", err);
        }
    };

    const fetchProduits = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/produits.php?include_stock=true`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                const produitsAvecStock = (result.data || []).map(p => ({
                    ...p,
                    stock_reel: p.stock_reel || 0,
                    seuil_alerte: p.seuil_alerte || 5,
                    prix_vente_ht: parseFloat(p.prix_vente_ht) || 0
                }));
                setProduits(produitsAvecStock);
            }
        } catch (err) {
            console.error("Erreur chargement produits", err);
        }
    };

    const ajouterLigne = (produit) => {
        const nouvelleLigne = {
            id_produit: produit.id_produit,
            reference: produit.reference,
            designation: produit.designation,
            quantite: 1,
            prix_unitaire_ht: produit.prix_vente_ht,
            tva_taux: taxes.tva_active ? taxes.tva_taux : 0,
            css_taux: taxes.css_active ? taxes.css_taux : 0,
            remise: 0
        };
        setDevis({ ...devis, lignes: [...devis.lignes, nouvelleLigne] });
        setShowProduitModal(false);
        setSearchProduit('');
        showToast('success', 'Produit ajouté', 'Le produit a été ajouté au devis');
    };

    const supprimerLigne = (index) => {
        const nouvellesLignes = devis.lignes.filter((_, i) => i !== index);
        setDevis({ ...devis, lignes: nouvellesLignes });
        showToast('info', 'Produit supprimé', 'La ligne a été retirée');
    };

    const updateLigne = (index, champ, valeur) => {
        const nouvellesLignes = [...devis.lignes];
        nouvellesLignes[index][champ] = valeur;
        setDevis({ ...devis, lignes: nouvellesLignes });
    };

    const calculerTotaux = () => {
        let total_ht = 0;
        let total_tva = 0;
        let total_css = 0;

        devis.lignes.forEach(ligne => {
            const montant_ligne_ht = ligne.quantite * ligne.prix_unitaire_ht * (1 - (ligne.remise || 0) / 100);
            total_ht += montant_ligne_ht;
            if (taxes.tva_active) total_tva += montant_ligne_ht * (ligne.tva_taux || taxes.tva_taux) / 100;
            if (taxes.css_active) total_css += montant_ligne_ht * (ligne.css_taux || taxes.css_taux) / 100;
        });

        const total_ttc = total_ht + total_tva + total_css;
        const net_a_payer = total_ttc - (devis.remise_globale || 0);
        return { total_ht, total_tva, total_css, total_ttc, net_a_payer };
    };

    const totaux = calculerTotaux();

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!devis.id_client) {
            showToast('error', 'Champ requis', 'Veuillez sélectionner un client');
            return;
        }
        if (devis.lignes.length === 0) {
            showToast('error', 'Champ requis', 'Veuillez ajouter au moins un produit');
            return;
        }

        setSaving(true);
        try {
            const userStr = localStorage.getItem("user");
            const user = userStr ? JSON.parse(userStr) : null;
            if (!user || !user.id_utilisateur) {
                showToast('error', 'Session', 'Utilisateur non connecté');
                return;
            }

            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/devis.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    action: 'create',
                    ...devis,
                    id_utilisateur: user.id_utilisateur
                })
            });

            const result = await response.json();

            if (result.success) {
                showToast('success', 'Devis créé', 'Le devis a été enregistré avec succès');
                setTimeout(() => navigate('/ventes/devisListe'), 2000);
            } else {
                showToast('error', 'Erreur', result.message || "Erreur lors de la création");
            }
        } catch (err) {
            showToast('error', 'Erreur de connexion', "Impossible de contacter le serveur");
        } finally {
            setSaving(false);
        }
    };

    const produitsFiltres = produits.filter(p => 
        p.designation?.toLowerCase().includes(searchProduit.toLowerCase()) ||
        p.reference?.toLowerCase().includes(searchProduit.toLowerCase())
    );

    /* ============================================================
       RENDU
       ============================================================ */
    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">

                    <ToastNotification
                        show={toast.show}
                        onClose={hideToast}
                        type={toast.type}
                        title={toast.title}
                        message={toast.message}
                    />

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-file-copy-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">Nouveau devis</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-file-list-line"></i>
                                        Proposition commerciale
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.secondary}15`, color: COLORS.secondary }}>
                                        <i className="ri-shopping-cart-line"></i>
                                        {devis.lignes.length} article(s)
                                    </span>
                                </div>
                            </div>
                        </div>
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => navigate('/ventes/devisListe')}
                            className="btn-modern-outline"
                        >
                            <i className="ri-arrow-left-line"></i> Retour à la liste
                        </Button>
                    </div>

                    <Form onSubmit={handleSubmit}>
                        <Row className="g-4">
                            {/* ===== COLONNE GAUCHE ===== */}
                            <Col lg={8}>

                                {/* Carte Informations générales */}
                                <Card className="border-0 shadow-sm rounded-3 mb-4">
                                    <Card.Header className="card-header-gradient">
                                        <h5 className="mb-0 text-white">
                                            <i className="ri-information-line me-2"></i>
                                            Informations générales
                                        </h5>
                                    </Card.Header>
                                    <Card.Body className="p-4">
                                        <Row className="g-3">
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">
                                                        Client <span className="text-danger">*</span>
                                                    </Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-user-line"></i></InputGroup.Text>
                                                        <Form.Select
                                                            value={devis.id_client}
                                                            onChange={(e) => setDevis({...devis, id_client: e.target.value})}
                                                            required
                                                            className="form-select-modern"
                                                        >
                                                            <option value="">Sélectionner un client</option>
                                                            {clients.map(client => (
                                                                <option key={client.id_client} value={client.id_client}>
                                                                    {client.raison_sociale} ({client.code_client})
                                                                </option>
                                                            ))}
                                                        </Form.Select>
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Date devis</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-calendar-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="date"
                                                            value={devis.date_proforma}
                                                            onChange={(e) => setDevis({...devis, date_proforma: e.target.value})}
                                                            required
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Date validité</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-calendar-check-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            type="date"
                                                            value={devis.date_validite}
                                                            onChange={(e) => setDevis({...devis, date_validite: e.target.value})}
                                                            required
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-modern">Notes</Form.Label>
                                                    <InputGroup className="input-modern">
                                                        <InputGroup.Text><i className="ri-sticky-note-line"></i></InputGroup.Text>
                                                        <Form.Control
                                                            as="textarea"
                                                            rows={2}
                                                            value={devis.notes}
                                                            onChange={(e) => setDevis({...devis, notes: e.target.value})}
                                                            placeholder="Conditions particulières, délais, etc..."
                                                        />
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>

                                {/* Carte Lignes */}
                                <Card className="border-0 shadow-sm rounded-3">
                                    <Card.Header className="card-header-gradient d-flex justify-content-between align-items-center">
                                        <h5 className="mb-0 text-white">
                                            <i className="ri-shopping-cart-line me-2"></i>
                                            Produits / Services
                                        </h5>
                                        <Button
                                            variant="light"
                                            size="sm"
                                            onClick={() => setShowProduitModal(true)}
                                            className="btn-header-light"
                                        >
                                            <i className="ri-add-line me-1"></i> Ajouter
                                        </Button>
                                    </Card.Header>
                                    <Card.Body className="p-0">
                                        {devis.lignes.length > 0 ? (
                                            <div className="table-responsive">
                                                <Table className="align-middle mb-0 table-charte">
                                                    <thead>
                                                        <tr>
                                                            <th className="ps-4">Référence</th>
                                                            <th>Désignation</th>
                                                            <th className="text-center" style={{ width: '100px' }}>Qté</th>
                                                            <th className="text-end" style={{ width: '120px' }}>Prix HT</th>
                                                            <th className="text-center" style={{ width: '100px' }}>Remise</th>
                                                            <th className="text-end" style={{ width: '120px' }}>Total HT</th>
                                                            <th className="text-center" style={{ width: '80px' }}>TVA</th>
                                                            <th className="text-center pe-4" style={{ width: '50px' }}></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {devis.lignes.map((ligne, index) => (
                                                            <tr key={index}>
                                                                <td className="ps-4">
                                                                    <span className="reference-badge-charte">
                                                                        <i className="ri-barcode-line"></i>{ligne.reference}
                                                                    </span>
                                                                </td>
                                                                <td className="fw-semibold">{ligne.designation}</td>
                                                                <td className="text-center">
                                                                    <Form.Control
                                                                        type="number"
                                                                        min="1"
                                                                        value={ligne.quantite}
                                                                        onChange={(e) => updateLigne(index, 'quantite', parseInt(e.target.value) || 1)}
                                                                        size="sm"
                                                                        className="text-center input-table-charte"
                                                                    />
                                                                </td>
                                                                <td className="text-end">
                                                                    <Form.Control
                                                                        type="number"
                                                                        step="0.01"
                                                                        value={ligne.prix_unitaire_ht}
                                                                        onChange={(e) => updateLigne(index, 'prix_unitaire_ht', parseFloat(e.target.value) || 0)}
                                                                        size="sm"
                                                                        className="text-end input-table-charte"
                                                                    />
                                                                </td>
                                                                <td className="text-center">
                                                                    <InputGroup size="sm" className="input-table-group-charte">
                                                                        <Form.Control
                                                                            type="number"
                                                                            min="0"
                                                                            max="100"
                                                                            value={ligne.remise || 0}
                                                                            onChange={(e) => updateLigne(index, 'remise', parseFloat(e.target.value) || 0)}
                                                                            className="text-center input-table-charte"
                                                                        />
                                                                        <InputGroup.Text className="input-table-suffix-charte">%</InputGroup.Text>
                                                                    </InputGroup>
                                                                </td>
                                                                <td className="text-end fw-bold" style={{ color: COLORS.primaryDark }}>
                                                                    {formatCurrency(ligne.quantite * ligne.prix_unitaire_ht * (1 - (ligne.remise || 0)/100))}
                                                                </td>
                                                                <td className="text-center">
                                                                    <span className="tva-badge-charte">
                                                                        {taxes.tva_active ? (ligne.tva_taux || taxes.tva_taux) : 0}%
                                                                    </span>
                                                                </td>
                                                                <td className="text-center pe-4">
                                                                    <Button
                                                                        variant="link"
                                                                        className="btn-delete-row-charte p-0"
                                                                        onClick={() => supprimerLigne(index)}
                                                                    >
                                                                        <i className="ri-delete-bin-line"></i>
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        ) : (
                                            <div className="empty-state-charte">
                                                <div className="empty-state-icon-charte">
                                                    <i className="ri-shopping-cart-line"></i>
                                                </div>
                                                <p className="text-muted mb-3">Aucun produit ajouté</p>
                                                <Button
                                                    onClick={() => setShowProduitModal(true)}
                                                    className="btn-modern-outline-primary"
                                                >
                                                    <i className="ri-add-line me-2"></i>
                                                    Ajouter un produit
                                                </Button>
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* ===== COLONNE DROITE : RÉCAPITULATIF ===== */}
                            <Col lg={4}>
                                <Card className="border-0 shadow-sm rounded-3 sticky-top" style={{ top: '20px' }}>
                                    <Card.Header className="card-header-gradient">
                                        <h5 className="mb-0 text-white">
                                            <i className="ri-calculator-line me-2"></i>
                                            Récapitulatif
                                        </h5>
                                    </Card.Header>
                                    <Card.Body className="p-4">
                                        <div className="recap-line-charte">
                                            <span className="text-muted">Total HT</span>
                                            <span className="fw-bold">{formatCurrency(totaux.total_ht)}</span>
                                        </div>
                                        {taxes.tva_active && totaux.total_tva > 0 && (
                                            <div className="recap-line-charte">
                                                <span className="text-muted">TVA ({taxes.tva_taux}%)</span>
                                                <span className="fw-semibold" style={{ color: COLORS.warning }}>
                                                    {formatCurrency(totaux.total_tva)}
                                                </span>
                                            </div>
                                        )}
                                        {taxes.css_active && totaux.total_css > 0 && (
                                            <div className="recap-line-charte">
                                                <span className="text-muted">CSS ({taxes.css_taux}%)</span>
                                                <span className="fw-semibold" style={{ color: COLORS.info }}>
                                                    {formatCurrency(totaux.total_css)}
                                                </span>
                                            </div>
                                        )}

                                        <div className="recap-total-charte">
                                            <span className="fw-bold">Total TTC</span>
                                            <span className="fw-bold" style={{ color: COLORS.primary, fontSize: '1.4rem' }}>
                                                {formatCurrency(totaux.total_ttc)}
                                            </span>
                                        </div>

                                        <div className="recap-inputs-box-charte mb-4">
                                            <Form.Group className="mb-0">
                                                <Form.Label className="form-label-modern">Remise globale (FCFA)</Form.Label>
                                                <InputGroup className="input-modern">
                                                    <InputGroup.Text><i className="ri-percent-line"></i></InputGroup.Text>
                                                    <Form.Control
                                                        type="number"
                                                        min="0"
                                                        value={devis.remise_globale}
                                                        onChange={(e) => setDevis({...devis, remise_globale: parseFloat(e.target.value) || 0})}
                                                    />
                                                </InputGroup>
                                            </Form.Group>
                                        </div>

                                        <div className="recap-net-charte">
                                            <span className="h6 fw-bold mb-0">Net à payer</span>
                                            <span className="h5 fw-bold mb-0" style={{ color: COLORS.success }}>
                                                {formatCurrency(totaux.net_a_payer)}
                                            </span>
                                        </div>

                                        <div className="d-grid gap-2 mt-4">
                                            <Button
                                                type="submit"
                                                disabled={saving}
                                                className="btn-modern-primary py-2"
                                            >
                                                {saving ? (
                                                    <><Spinner size="sm" className="me-2" />Création...</>
                                                ) : (
                                                    <><i className="ri-save-line me-2"></i>Créer le devis</>
                                                )}
                                            </Button>
                                            <Button
                                                variant="outline-secondary"
                                                onClick={() => navigate('/ventes/devisListe')}
                                                className="btn-modern-outline w-100 justify-content-center"
                                            >
                                                Annuler
                                            </Button>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Form>
                </div>
                <Footer />
            </div>

            {/* ===== MODAL PRODUITS ===== */}
            <Modal show={showProduitModal} onHide={() => setShowProduitModal(false)} size="lg" centered className="modal-charte">
                <Modal.Header closeButton className="modal-header-charte" style={{ background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})` }}>
                    <div className="d-flex align-items-center gap-3">
                        <div className="modal-header-icon">
                            <i className="ri-shopping-cart-line"></i>
                        </div>
                        <div>
                            <Modal.Title className="fs-5 fw-bold text-white mb-0">Sélectionner un produit</Modal.Title>
                            <p className="text-white-50 mb-0 small">Choisissez parmi la liste des produits disponibles</p>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body className="p-4">
                    <InputGroup className="input-modern mb-4">
                        <InputGroup.Text><i className="ri-search-line"></i></InputGroup.Text>
                        <Form.Control
                            type="text"
                            placeholder="Rechercher un produit par référence ou désignation..."
                            value={searchProduit}
                            onChange={(e) => setSearchProduit(e.target.value)}
                        />
                    </InputGroup>

                    <div className="table-responsive">
                        <Table hover className="align-middle mb-0 table-charte">
                            <thead>
                                <tr>
                                    <th className="ps-4">Référence</th>
                                    <th>Désignation</th>
                                    <th className="text-end">Prix HT</th>
                                    <th className="text-center">Stock</th>
                                    <th className="pe-4"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {produitsFiltres.map(produit => (
                                    <tr key={produit.id_produit}>
                                        <td className="ps-4">
                                            <span className="reference-badge-charte">
                                                <i className="ri-barcode-line"></i>{produit.reference}
                                            </span>
                                        </td>
                                        <td className="fw-semibold">{produit.designation}</td>
                                        <td className="text-end">{formatCurrency(produit.prix_vente_ht)}</td>
                                        <td className="text-center">
                                            <span className={produit.stock_reel < produit.seuil_alerte ? 'text-danger fw-bold' : 'text-muted'}>
                                                {produit.stock_reel || 0}
                                            </span>
                                            {produit.stock_reel <= 0 && (
                                                <i className="ri-close-circle-line text-danger ms-1" title="Rupture de stock"></i>
                                            )}
                                            {produit.stock_reel > 0 && produit.stock_reel < produit.seuil_alerte && (
                                                <i className="ri-alert-line text-warning ms-1" title="Stock critique"></i>
                                            )}
                                        </td>
                                        <td className="pe-4">
                                            <Button
                                                size="sm"
                                                onClick={() => ajouterLigne(produit)}
                                                disabled={produit.stock_reel <= 0}
                                                className="btn-add-product-charte"
                                            >
                                                <i className="ri-add-line"></i>
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                                {produitsFiltres.length === 0 && (
                                    <tr>
                                        <td colSpan="5" className="text-center py-4">
                                            <i className="ri-search-line fs-3 text-muted d-block mb-2"></i>
                                            <span className="text-muted small">Aucun produit ne correspond à votre recherche</span>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0 pb-4 px-4 pt-0">
                    <Button
                        variant="outline-secondary"
                        onClick={() => setShowProduitModal(false)}
                        className="btn-modern-outline"
                    >
                        Fermer
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white;
                    padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    flex-wrap: wrap;
                    gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }
                .card-header-gradient {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }
                .btn-header-light {
                    background: rgba(255,255,255,0.2);
                    border: 1px solid rgba(255,255,255,0.3);
                    color: white; border-radius: 50px;
                    padding: 0.35rem 0.9rem;
                    font-size: 0.8rem; font-weight: 500;
                    transition: all 0.2s ease;
                }
                .btn-header-light:hover {
                    background: rgba(255,255,255,0.3);
                    color: white; transform: translateY(-1px);
                }

                /* ===== INPUTS ===== */
                .form-label-modern {
                    font-weight: 600; color: #334155;
                    font-size: 0.85rem; margin-bottom: 0.4rem;
                }
                .input-modern .input-group-text {
                    background: #f8fafc; border: 2px solid #e2e8f0;
                    border-right: none; color: var(--primary);
                    border-radius: 10px 0 0 10px;
                }
                .input-modern .form-control,
                .input-modern .form-select {
                    border: 2px solid #e2e8f0; border-left: none;
                    border-radius: 0 10px 10px 0;
                    padding: 0.6rem 0.9rem; font-size: 0.9rem;
                    transition: all 0.2s ease;
                }
                .input-modern .form-control:focus,
                .input-modern .form-select:focus {
                    border-color: var(--secondary);
                    box-shadow: 0 0 0 3px rgba(33,172,205,0.1);
                }
                .form-select-modern { background-color: white; cursor: pointer; }

                /* ===== TABLE ===== */
                .table-charte thead th {
                    background: #f8fafc; color: #475569;
                    font-weight: 600; font-size: 0.7rem;
                    text-transform: uppercase; letter-spacing: 0.5px;
                    border-bottom: 2px solid #e2e8f0;
                    padding: 0.9rem 0.75rem;
                }
                .table-charte tbody td {
                    padding: 0.85rem 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                }
                .table-charte tbody tr { transition: background 0.15s ease; }
                .table-charte tbody tr:hover { background: #f8fafc; }

                .reference-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    background: #f1f5f9; padding: 0.25rem 0.6rem;
                    border-radius: 6px; font-size: 0.7rem;
                    font-family: monospace; color: #475569;
                }
                .tva-badge-charte {
                    display: inline-flex; align-items: center;
                    background: #fef3c7; color: #92400e;
                    padding: 0.25rem 0.6rem; border-radius: 50px;
                    font-size: 0.7rem; font-weight: 600;
                }
                .input-table-charte {
                    border: 1.5px solid #e2e8f0;
                    border-radius: 8px;
                    font-size: 0.85rem;
                    padding: 0.35rem 0.5rem;
                    transition: all 0.2s ease;
                }
                .input-table-charte:focus {
                    border-color: var(--secondary);
                    box-shadow: 0 0 0 3px rgba(33,172,205,0.1);
                }
                .input-table-group-charte .input-table-charte {
                    border-top-right-radius: 0;
                    border-bottom-right-radius: 0;
                    border-right: none;
                }
                .input-table-suffix-charte {
                    background: #f1f5f9;
                    border: 1.5px solid #e2e8f0;
                    border-left: none;
                    border-radius: 0 8px 8px 0;
                    color: #64748b;
                    font-size: 0.75rem;
                    font-weight: 600;
                    padding: 0.35rem 0.5rem;
                }
                .btn-delete-row-charte {
                    color: var(--danger);
                    background: transparent;
                    border: none;
                    font-size: 1rem;
                    transition: all 0.2s ease;
                }
                .btn-delete-row-charte:hover {
                    color: #b91c1c;
                    transform: scale(1.1);
                }
                .btn-add-product-charte {
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    border: none; color: white;
                    width: 34px; height: 34px; border-radius: 50%;
                    display: inline-flex; align-items: center; justify-content: center;
                    transition: all 0.2s ease;
                    box-shadow: 0 2px 8px rgba(41,90,167,0.25);
                }
                .btn-add-product-charte:hover {
                    transform: scale(1.1);
                    box-shadow: 0 4px 12px rgba(41,90,167,0.4);
                    color: white;
                }
                .btn-add-product-charte:disabled {
                    background: #cbd5e1 !important;
                    cursor: not-allowed;
                    transform: none;
                    box-shadow: none;
                }

                /* ===== EMPTY STATE ===== */
                .empty-state-charte {
                    text-align: center; padding: 3rem 2rem;
                }
                .empty-state-icon-charte {
                    width: 80px; height: 80px;
                    background: linear-gradient(135deg, rgba(41,90,167,0.08), rgba(33,172,205,0.12));
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 1rem; font-size: 2rem; color: var(--secondary);
                }
                .btn-modern-outline-primary {
                    border: 1.5px solid var(--secondary);
                    background: white; color: var(--secondary);
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 600;
                    transition: all 0.2s ease;
                }
                .btn-modern-outline-primary:hover {
                    background: var(--secondary); color: white;
                    transform: translateY(-1px);
                    box-shadow: 0 4px 12px rgba(33,172,205,0.3);
                }

                /* ===== RÉCAP ===== */
                .recap-line-charte {
                    display: flex; justify-content: space-between; align-items: center;
                    padding: 0.5rem 0; border-bottom: 1px dashed #e2e8f0;
                    font-size: 0.85rem;
                }
                .recap-line-charte:last-of-type { border-bottom: none; }
                .recap-total-charte {
                    display: flex; justify-content: space-between; align-items: center;
                    padding: 0.85rem 1rem;
                    background: linear-gradient(135deg, #f8fafc, #ffffff);
                    border-radius: 12px;
                    margin-top: 0.5rem;
                    border: 1px solid #e2e8f0;
                }
                .recap-inputs-box-charte {
                    background: #f8fafc; padding: 1rem;
                    border-radius: 12px; border: 1px solid #f1f5f9;
                    margin-top: 1rem;
                }
                .recap-net-charte {
                    display: flex; justify-content: space-between; align-items: center;
                    padding: 0.85rem 1rem;
                    border-top: 2px solid #e2e8f0;
                    margin-top: 0.5rem;
                }

                /* ===== BOUTONS ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-primary {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border: none; color: white; padding: 0.6rem 2rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.9rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                    display: inline-flex; align-items: center; justify-content: center;
                    gap: 6px;
                }
                .btn-modern-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(41,90,167,0.4);
                    color: white;
                }
                .btn-modern-primary:disabled { opacity: 0.7; transform: none; }

                /* ===== MODAL ===== */
                .modal-charte .modal-content {
                    border: none; border-radius: 20px; overflow: hidden;
                    box-shadow: 0 30px 80px rgba(0,0,0,0.15);
                }
                .modal-header-charte { padding: 1.25rem 1.5rem; border: none; }
                .modal-header-charte .btn-close {
                    filter: brightness(0) invert(1); opacity: 0.8;
                }
                .modal-header-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(255,255,255,0.2);
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.5rem; color: white;
                }

                /* ===== TOAST ===== */
                .toast-notification-fixed {
                    position: fixed; bottom: 24px; right: 24px;
                    z-index: 9999; animation: slideInRight 0.3s ease-out;
                }
                .toast-modern {
                    background: white; border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    min-width: 320px; max-width: 400px; overflow: hidden;
                }
                .toast-header-custom {
                    padding: 12px 20px;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .toast-close-btn {
                    background: transparent; border: none;
                    color: white; cursor: pointer; opacity: 0.8;
                }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }

                /* ===== ANIMATIONS ===== */
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}